import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown } from "lucide-react";

// Table cell input with a fixed option list (filter while typing, free text allowed).
export const ComboCell = ({ value, options, onChange, onKeyDown, testId, className, ...rest }) => {
    const ref = useRef(null);
    const [open, setOpen] = useState(false);
    const [nav, setNav] = useState(false);
    const [filt, setFilt] = useState(false);
    const [hi, setHi] = useState(0);
    const [pos, setPos] = useState(null);
    const text = String(value ?? "");
    const list = filt && text ? options.filter((o) => o.toLowerCase().includes(text.toLowerCase())) : options;

    const show = () => {
        const r = ref.current.getBoundingClientRect();
        setPos({ left: r.left, top: r.bottom + 2, width: Math.max(r.width, 150) });
        setOpen(true);
    };
    const close = () => { setOpen(false); setNav(false); setFilt(false); setHi(0); };
    const pick = (o) => { onChange(o); close(); };

    useEffect(() => {
        if (!open) return;
        const onScroll = (e) => { if (!e.target?.closest?.("[data-combo-list]")) close(); };
        window.addEventListener("scroll", onScroll, true);
        return () => window.removeEventListener("scroll", onScroll, true);
    }, [open]);

    const keyDown = (e) => {
        if (e.altKey && e.key === "ArrowDown") { e.preventDefault(); setFilt(false); setHi(options.indexOf(text)); show(); setNav(true); return; }
        if (open && nav && list.length) {
            if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(list.length - 1, h + 1)); return; }
            if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(0, h - 1)); return; }
            if (e.key === "Enter" && hi >= 0) { e.preventDefault(); pick(list[Math.min(hi, list.length - 1)]); return; }
        }
        if (e.key === "Escape" && open) { close(); return; }
        if (["Tab", "Enter", "ArrowUp", "ArrowDown"].includes(e.key) || e.shiftKey) close();
        onKeyDown(e);
    };

    return (
        <div className="relative flex items-center">
            <input ref={ref} data-testid={testId} value={text} {...rest}
                onBlur={close}
                onKeyDown={keyDown}
                onChange={(e) => { onChange(e.target.value); setNav(true); setFilt(true); setHi(0); if (!open) show(); }}
                className={`${className} pr-5`} />
            <button type="button" tabIndex={-1} data-testid={`${testId}-arrow`}
                onMouseDown={(e) => {
                    e.preventDefault();
                    ref.current.focus();
                    if (open) close(); else { setNav(true); setFilt(false); setHi(options.indexOf(text)); show(); }
                }}
                className="absolute right-0.5 top-1/2 -translate-y-1/2 w-4 h-5 flex items-center justify-center rounded-sm text-slate-400 hover:text-white hover:bg-[#1e293b] border border-transparent hover:border-[#334155] transition-colors">
                <ChevronDown className="w-3 h-3" />
            </button>
            {open && pos && list.length > 0 && createPortal(
                <div data-combo-list data-testid={`${testId}-options`}
                    style={{ position: "fixed", left: pos.left, top: pos.top, minWidth: pos.width }}
                    className="z-[100] max-h-56 overflow-auto rounded-md border border-[#1e293b] bg-[#111827] py-1 shadow-xl">
                    {list.map((o, i) => (
                        <div key={o} data-testid={`${testId}-option-${i}`}
                            onMouseDown={(e) => { e.preventDefault(); pick(o); }}
                            onMouseEnter={() => setHi(i)}
                            className={`flex items-center justify-between gap-3 px-2.5 py-1.5 text-sm cursor-pointer text-slate-200 transition-colors ${nav && i === hi ? "bg-blue-600/40" : "hover:bg-[#182232]"}`}>
                            <span>{o}</span>
                            {o === text && <Check className="w-3.5 h-3.5 text-blue-400" />}
                        </div>
                    ))}
                </div>,
                document.body,
            )}
        </div>
    );
};
